import { useMemo } from "react";
import { RideSummary } from "../ride-center/components/RideSummary";
import { RideAnalysisChart } from "../ride-center/components/RideAnalysisChart";
import { RideAnalysisChartBoundary } from "../ride-center/components/RideAnalysisChartBoundary";
import { DevelopmentAnalyticsPanel } from "../ride-center/components/DevelopmentAnalyticsPanel";
import type { Ride } from "../ride-center/types/ride";
import type { FtpSelectionResult } from "../athlete-profile/ftpSelection";
import { useAthleteProfile } from "../athlete-profile/AthleteProfileProvider";
import type { ActivitySession } from "./activitySources";
import type { UnifiedActivityAnalysisResult } from "./sessionAnalysis";
import type { PersonalBaselineResult } from "./personalBaseline";
import { SessionAnalysisSections } from "./components/SessionAnalysisSections";
import { CoachSummaryCard } from "./components/CoachSummaryCard";
import { CoachPrioritiesCard } from "./components/CoachPrioritiesCard";
import { PersonalBaselineCard } from "./components/PersonalBaselineCard";
import { ContextTimeline } from "../context/components/ContextTimeline";
import { getContextSummary, getRelatedContext, type AthleteTimelineEvent } from "./athleteTimeline";

type RideAnalysisWorkspaceProps = {
  ride: Ride;
  parsedRide: Ride | null;
  ftpSelection: FtpSelectionResult | null;
  analysisFtpSelection: FtpSelectionResult | null;
  selectedIntervalId: string | null;
  onSelectedIntervalChange: (intervalId: string | null) => void;
  isParsingRide: boolean;
  rideParseError: string | null;
  onBackToTrain: () => void;
  onReanalyzeWithCurrentFtp: () => void;
  activitySession: ActivitySession;
  unifiedAnalysis: UnifiedActivityAnalysisResult | null;
  personalBaseline: PersonalBaselineResult | null;
  contextEvents?: AthleteTimelineEvent[];
  onOpenRelatedActivityFromContext?: (activityId: string) => void;
  onOpenDailyHealthDateFromContext?: (localDate: string) => void;
  restoredAnalysisMessage?: string | null;
};

function resolveAnalysisAnchorTimestamp(ride: Ride, parsedRide: Ride | null): string | null {
  const candidates = [parsedRide?.rideDate, ride.rideDate];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && !Number.isNaN(Date.parse(candidate))) {
      return new Date(candidate).toISOString();
    }
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return new Date(candidate).toISOString();
    }
    if (candidate instanceof Date && !Number.isNaN(candidate.getTime())) {
      return candidate.toISOString();
    }
  }
  return null;
}

export function RideAnalysisWorkspace({
  ride,
  parsedRide,
  ftpSelection,
  analysisFtpSelection,
  selectedIntervalId,
  onSelectedIntervalChange,
  isParsingRide,
  rideParseError,
  onBackToTrain,
  onReanalyzeWithCurrentFtp,
  activitySession,
  unifiedAnalysis,
  personalBaseline,
  contextEvents = [],
  onOpenRelatedActivityFromContext,
  onOpenDailyHealthDateFromContext,
  restoredAnalysisMessage
}: RideAnalysisWorkspaceProps) {
  const { athleteProfile } = useAthleteProfile();
  const currentFtpSelection = ftpSelection ?? analysisFtpSelection;
  const activeFtpSelection = analysisFtpSelection ?? currentFtpSelection;
  const hasCurrentOverrideChanged = Boolean(currentFtpSelection && analysisFtpSelection && currentFtpSelection.ftpWatts !== analysisFtpSelection.ftpWatts);
  const contextAnchorTimestamp = resolveAnalysisAnchorTimestamp(ride, parsedRide);
  const relatedContextSummary = useMemo(() => {
    if (!contextAnchorTimestamp) return [];
    return getContextSummary(contextEvents, {
      timestamp: contextAnchorTimestamp,
      excludeTypes: ["ride", "ride_analysis", "activity_completed", "health_import"],
      limit: 6,
      maxHours: 36,
      preferredUnits: athleteProfile.personal.preferredUnits
    });
  }, [athleteProfile.personal.preferredUnits, contextAnchorTimestamp, contextEvents]);
  const relatedContextTimeline = useMemo(() => {
    if (!contextAnchorTimestamp) return [];
    return getRelatedContext(contextEvents, {
      timestamp: contextAnchorTimestamp,
      excludeTypes: ["ride_analysis", "activity_completed", "health_import"],
      limit: 8,
      maxHours: 36
    }).relatedEvents;
  }, [contextAnchorTimestamp, contextEvents]);

  return (
    <section className="ride-analysis-workspace full-width-analysis">
      <header className="ride-analysis-workspace-header">
        <div>
          <p className="eyebrow">RIDE ANALYSIS</p>
          <h2>Full ride analysis workspace</h2>
          <p className="command-note">FTP reference: {activeFtpSelection?.ftpWatts ?? "--"} W</p>
          <p className="command-note">Source: {activeFtpSelection?.sourceLabel ?? "--"}</p>
          <p className="command-note">Effective date: {activeFtpSelection?.effectiveDateIso ? new Date(activeFtpSelection.effectiveDateIso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "--"}</p>
          {hasCurrentOverrideChanged ? <p className="notice">This analysis used an older FTP snapshot.</p> : null}
        </div>
        <div className="ride-analysis-workspace-actions">
          {hasCurrentOverrideChanged ? <button type="button" className="ride-analysis-button primary-action" onClick={onReanalyzeWithCurrentFtp}>Reanalyze with current FTP</button> : null}
          <button type="button" className="ride-analysis-button secondary-action" onClick={onBackToTrain}>Back to Train</button>
        </div>
      </header>

      {restoredAnalysisMessage && <p className="notice" role="status">{restoredAnalysisMessage}</p>}

      <details className="analysis-source-summary">
        <summary>Included Sources · {(activitySession.primary ? 1 : 0) + activitySession.supportingRideSources.length + activitySession.companionSources.length}</summary>
        <ul>
          {activitySession.primary && <li><strong>{activitySession.primary.type}</strong><span>{activitySession.primary.file.name}</span></li>}
          {[...activitySession.supportingRideSources, ...activitySession.companionSources].map(source => <li key={source.id}><strong>{source.type}</strong><span>{source.file.name}</span></li>)}
        </ul>
      </details>

      {unifiedAnalysis?.coachPriority ? <CoachPrioritiesCard summary={unifiedAnalysis.coachPriority} /> : null}

      {personalBaseline ? <PersonalBaselineCard baseline={personalBaseline} /> : null}

      {unifiedAnalysis?.coachSummary ? <CoachSummaryCard summary={unifiedAnalysis.coachSummary} /> : null}

      {unifiedAnalysis && <SessionAnalysisSections analysis={unifiedAnalysis} />}

      {unifiedAnalysis ? (
        <section className="analysis-module" aria-labelledby="related-context-title">
          <div className="analysis-module-heading">
            <div>
              <p className="eyebrow">RELATED CONTEXT</p>
              <h2 id="related-context-title">Related Context</h2>
            </div>
          </div>
          {relatedContextSummary.length === 0 ? (
            <p className="command-note">No linked context has been recorded near this analysis yet.</p>
          ) : (
            <ul className="source-provenance-list">
              {relatedContextSummary.map(item => <li key={item.id}>{item.label}</li>)}
            </ul>
          )}
          <ContextTimeline
            events={relatedContextTimeline}
            emptyMessage="No linked context events to display."
            onOpenRelatedActivity={onOpenRelatedActivityFromContext}
            onOpenDailyHealthDate={onOpenDailyHealthDateFromContext}
            preferredUnits={athleteProfile.personal.preferredUnits}
          />
        </section>
      ) : null}

      <RideSummary
        ride={ride}
        preferredUnits={athleteProfile.personal.preferredUnits}
        isParsingRide={isParsingRide}
        rideParseError={rideParseError}
      />

      <div className="ride-analysis-main-content">
        <RideAnalysisChartBoundary>
          <RideAnalysisChart
            ride={parsedRide}
            ftpWatts={activeFtpSelection?.ftpWatts ?? null}
            selectedIntervalId={selectedIntervalId}
            onSelectedIntervalChange={onSelectedIntervalChange}
            isParsingRide={isParsingRide}
            rideParseError={rideParseError}
            respiratoryPoints={unifiedAnalysis?.respiratoryAnalysis?.records.map(record => ({ elapsedSeconds: record.elapsedSeconds, minuteVentilation: record.minuteVentilation, breathingFrequency: record.breathingFrequency }))}
          />
        </RideAnalysisChartBoundary>
      </div>

      <details className="dev-diagnostics">
        <summary>Developer Diagnostics</summary>
        {personalBaseline ? (
          <details className="analysis-source-summary">
            <summary>Baseline diagnostics</summary>
            <dl>
              <div><dt>Total comparable candidates considered</dt><dd>{personalBaseline.diagnostics.comparableCandidatesConsidered}</dd></div>
              <div><dt>Selected baseline window</dt><dd>{personalBaseline.diagnostics.selectedWindow ?? "none"}</dd></div>
              <div><dt>Baseline confidence</dt><dd>{personalBaseline.baselineConfidence}</dd></div>
              <div><dt>Trend method</dt><dd>{personalBaseline.diagnostics.trendMethod}</dd></div>
              <div><dt>Confidence reason</dt><dd>{personalBaseline.diagnostics.confidenceReason}</dd></div>
              <div>
                <dt>Inclusion by candidate</dt>
                <dd>{personalBaseline.diagnostics.inclusion.map(item => `${item.activityId}: ${item.included ? "included" : "excluded"} (${item.score})`).join(" | ") || "none"}</dd>
              </div>
              <div>
                <dt>Valid sample counts per metric</dt>
                <dd>{personalBaseline.diagnostics.validSampleCounts.map(item => `${item.metricName}=${item.sampleCount}`).join(" | ") || "none"}</dd>
              </div>
            </dl>
          </details>
        ) : null}
        {import.meta.env.DEV && parsedRide ? (
          <DevelopmentAnalyticsPanel
            ride={parsedRide}
            ftpSelection={currentFtpSelection}
            analysisFtpSelection={analysisFtpSelection}
            selectedIntervalId={selectedIntervalId}
            onIntervalSelect={onSelectedIntervalChange}
          />
        ) : (
          <p className="command-note">Diagnostics become available when a parsed ride exists in development mode.</p>
        )}
      </details>
    </section>
  );
}
